/**
 * FloatingInea — the always-on bubble + chat panel.
 *
 * Mounted at the App root so Inea is reachable from every page (except the
 * onboarding flow). Knows the current route + any open prospect to power
 * contextual answers and tool-driven actions.
 */
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Loader2,
  Send,
  X,
  Sparkles,
  RefreshCw,
  Copy,
  Check,
  Phone,
  MessageCircle,
  Lightbulb,
  Target,
} from 'lucide-react';
import { useInea, type ChatTurn, type InlineTool } from '@/hooks/useInea';
import { useIneaContext } from '@/components/IneaContext';
import { useTranslation } from 'react-i18next';
import { useDiagnostic } from '@/hooks/useDiagnostic';
import { QuotaReachedNotice } from '@/components/QuotaReachedNotice';
import { IneaMark } from '@/components/IneaMark';

interface Suggestion { labelKey: string; promptKey: string; }
const SUGGESTED: Suggestion[] = [
  { labelKey: 'inea.suggestionRelance', promptKey: 'inea.suggestionRelancePrompt' },
  { labelKey: 'inea.suggestionPost', promptKey: 'inea.suggestionPostPrompt' },
  { labelKey: 'inea.suggestionPipeline', promptKey: 'inea.suggestionPipelinePrompt' },
];

/** Maps URL → human page hint sent to the API. */
function hintFromPath(path: string): string {
  if (path === '/') return 'dashboard';
  if (path.startsWith('/prospects')) return 'pipeline';
  if (path.startsWith('/audit')) return 'audit prospect';
  if (path.startsWith('/identite')) return 'identité digitale';
  if (path.startsWith('/contenu')) return 'stratégie de contenu';
  if (path.startsWith('/diagnostic')) return 'diagnostic';
  if (path.startsWith('/studio')) return 'studio visuel';
  if (path.startsWith('/bibliotheque')) return 'bibliothèque';
  if (path.startsWith('/relances')) return 'tracker de relances';
  if (path.startsWith('/profil')) return 'profil';
  return path;
}

export function FloatingInea() {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const { t } = useTranslation();
  const { turns, ask, busy, error, quotaReached, reset } = useInea();
  // Lot 1 — sans diagnostic (membre Mars arrivé par le SSO), Inéa propose l'entretien.
  const { diagnostic, isLoading: diagnosticLoading } = useDiagnostic();
  const { openProspectId } = useIneaContext();
  const location = useLocation();
  const scrollRef = useRef<HTMLDivElement>(null);

  // Hide on auth/onboarding routes — Inea is only useful inside the app.
  const hidden =
    location.pathname.startsWith('/login') || location.pathname.startsWith('/onboarding');

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [turns]);

  if (hidden) return null;

  function submit(): void {
    const msg = draft.trim();
    if (!msg) return;
    setDraft('');
    void ask(msg, {
      pageHint: hintFromPath(location.pathname),
      openProspectId: openProspectId ?? undefined,
    });
  }

  return (
    <>
      {/* Floating bubble */}
      <AnimatePresence>
        {!open && (
          <motion.button
            initial={{ opacity: 0, scale: 0.7, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.7, y: 10 }}
            transition={{ type: 'spring', damping: 22, stiffness: 320 }}
            onClick={() => setOpen(true)}
            className="fixed bottom-[104px] right-4 z-50 flex items-center justify-center rounded-full bg-gradient-to-br from-blue via-violet to-pink p-1 shadow-[0_18px_40px_-12px_rgba(168,85,247,0.55)] transition hover:shadow-[0_22px_50px_-12px_rgba(168,85,247,0.7)] sm:bottom-5 sm:right-5 sm:gap-2.5"
            aria-label={t('inea.openLabel') as string}
          >
            <span className="flex h-12 w-12 items-center justify-center overflow-hidden rounded-full bg-white">
              <IneaMark size={42} />
            </span>
            <span className="hidden pr-4 font-display text-[13px] font-800 text-white sm:inline">
              {t('inea.openLabel')}
            </span>
            {/* Animated ping ring */}
            <span className="pointer-events-none absolute -inset-1 rounded-full opacity-40">
              <span className="absolute inset-0 animate-ping rounded-full bg-pink/30" />
            </span>
          </motion.button>
        )}
      </AnimatePresence>

      {/* Chat panel */}
      <AnimatePresence>
        {open && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setOpen(false)}
              className="fixed inset-0 z-40 bg-night/30 backdrop-blur-[2px]"
            />
            <motion.aside
              initial={{ opacity: 0, y: 24, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.97 }}
              transition={{ type: 'spring', damping: 28, stiffness: 320 }}
              className="fixed inset-x-3 bottom-[100px] top-3 z-50 flex flex-col overflow-hidden rounded-[24px] border border-white/40 bg-cream/95 shadow-[0_30px_80px_-20px_rgba(10,18,38,0.45)] backdrop-blur-xl sm:inset-auto sm:bottom-5 sm:right-5 sm:h-[min(720px,80vh)] sm:w-[min(440px,calc(100vw-2rem))]"
            >
              {/* Header */}
              <div className="relative flex shrink-0 items-center justify-between gap-3 border-b border-ink/[0.06] bg-gradient-to-br from-blue/[0.08] via-violet/[0.06] to-pink/[0.06] px-4 py-3">
                <div className="flex items-center gap-2.5">
                  <IneaMark size={38} />
                  <div>
                    <p className="font-display text-[14px] font-800 leading-none tracking-[-0.015em]">
                      Inea
                    </p>
                    <p className="mt-0.5 flex items-center gap-1 font-mono text-[9.5px] font-700 uppercase tracking-[0.14em] text-pink">
                      <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-pink" />
                      {t('inea.online')} · {hintFromPath(location.pathname)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  {turns.length > 0 && (
                    <button
                      onClick={reset}
                      className="rounded-lg bg-white/60 p-1.5 text-ink/55 transition hover:text-ink"
                      aria-label={t('inea.newConversation') as string}
                      title={t('inea.newConversation') as string}
                    >
                      <RefreshCw size={13} />
                    </button>
                  )}
                  <button
                    onClick={() => setOpen(false)}
                    className="rounded-lg bg-white/60 p-1.5 text-ink/55 transition hover:text-ink"
                    aria-label={t('inea.close') as string}
                  >
                    <X size={14} />
                  </button>
                </div>
              </div>

              {/* Body */}
              <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4">
                {turns.length === 0 ? (
                  <>
                    {!diagnostic && !diagnosticLoading && (
                      <p className="mb-3 rounded-xl bg-violet/10 px-3 py-2 text-[12px] leading-[1.5] text-ink/70">
                        {t('interview.ineaNudge')}
                      </p>
                    )}
                    <Welcome onPick={(p) => void ask(p, { pageHint: hintFromPath(location.pathname), openProspectId: openProspectId ?? undefined })} />
                  </>
                ) : (
                  <div className="space-y-3">
                    {turns.map((t, i) => (
                      <TurnView key={i} turn={t} />
                    ))}
                    {quotaReached && <QuotaReachedNotice />}
                    {error && !quotaReached && (
                      <p className="rounded-xl bg-pink/10 px-3 py-2 text-[12px] font-600 text-pink-deep">
                        {error}
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Suggestion chips */}
              {turns.length === 0 && (
                <div className="shrink-0 border-t border-ink/[0.06] bg-white/40 px-3 py-2.5">
                  <div className="flex flex-wrap gap-1.5">
                    {SUGGESTED.map((s) => (
                      <button
                        key={s.labelKey}
                        onClick={() =>
                          void ask(t(s.promptKey), {
                            pageHint: hintFromPath(location.pathname),
                            openProspectId: openProspectId ?? undefined,
                          })
                        }
                        className="inline-flex items-center gap-1 rounded-full border border-ink/[0.10] bg-white px-2.5 py-1 text-[11.5px] font-700 text-ink/70 transition hover:border-pink/40 hover:text-pink"
                      >
                        <Sparkles size={10} /> {t(s.labelKey)}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Input */}
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  submit();
                }}
                className="flex shrink-0 items-center gap-2 border-t border-ink/[0.06] bg-white/85 px-3 py-3"
              >
                <input
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  placeholder={t('inea.placeholder') as string}
                  disabled={busy}
                  className="flex-1 rounded-full border border-ink/[0.10] bg-white px-3.5 py-2 text-[13px] text-ink outline-none transition focus:border-pink/60 focus:ring-2 focus:ring-pink/20 disabled:opacity-60"
                />
                <button
                  type="submit"
                  disabled={busy || draft.trim().length === 0}
                  className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-blue via-violet to-pink text-white shadow-[0_6px_18px_-6px_rgba(168,85,247,0.6)] transition disabled:opacity-40"
                  aria-label={t('inea.send') as string}
                >
                  {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={13} />}
                </button>
              </form>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
}

function Welcome({ onPick }: { onPick: (prompt: string) => void }) {
  const { t } = useTranslation();
  return (
    <div className="py-2">
      <div className="mb-3 flex items-start gap-2.5">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-blue to-pink text-white">
          <IneaMark size={26} />
        </span>
        <div className="rounded-2xl rounded-tl-md bg-white/90 px-3.5 py-2.5 text-[13.5px] leading-[1.5] text-ink/85 shadow-[0_2px_8px_-4px_rgba(10,18,38,0.2)]">
          {t('inea.welcomeIntro')}
        </div>
      </div>
      <p className="mb-2 font-mono text-[9.5px] font-700 uppercase tracking-[0.14em] text-ink/45">
        {t('inea.quickStart')}
      </p>
      <div className="space-y-1.5">
        {SUGGESTED.map((s) => (
          <button
            key={s.labelKey}
            onClick={() => onPick(t(s.promptKey))}
            className="flex w-full items-center gap-2 rounded-xl border border-ink/[0.08] bg-white/70 px-3 py-2 text-left text-[12.5px] font-600 text-ink/75 transition hover:border-pink/30 hover:text-ink"
          >
            <Sparkles size={11} className="text-pink" />
            {t(s.promptKey)}
          </button>
        ))}
      </div>
    </div>
  );
}

function TurnView({ turn }: { turn: ChatTurn }) {
  if (turn.role === 'user') {
    return (
      <div className="flex justify-end">
        <div className="max-w-[85%] rounded-2xl rounded-br-md bg-gradient-to-br from-blue to-violet px-3.5 py-2 text-[13.5px] leading-[1.45] text-white shadow-[0_4px_12px_-6px_rgba(59,130,246,0.5)]">
          {turn.text}
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-start gap-2.5">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-blue to-pink text-white">
        <IneaMark size={26} />
      </span>
      <div className="flex max-w-[85%] flex-col gap-2.5">
        {turn.text && (
          <div className="rounded-2xl rounded-tl-md bg-white/90 px-3.5 py-2.5 text-[13.5px] leading-[1.5] text-ink/85 shadow-[0_2px_8px_-4px_rgba(10,18,38,0.2)]">
            {turn.text}
            {turn.streaming && <span className="ml-1 inline-block h-3 w-1 animate-pulse bg-pink align-baseline" />}
          </div>
        )}
        {(turn.tools ?? []).map((t, i) => (
          <ToolResult key={i} tool={t} />
        ))}
      </div>
    </div>
  );
}

// ── Tool result cards ────────────────────────────────────────────────────────

function ToolResult({ tool }: { tool: InlineTool }) {
  if (tool.name === 'give_post_idea') {
    return <PostIdeaCard payload={tool.payload as PostIdeaPayload} />;
  }
  if (tool.name === 'suggest_replies') {
    return <RepliesCard payload={tool.payload as RepliesPayload} />;
  }
  if (tool.name === 'suggest_call_script') {
    return <CallScriptCard payload={tool.payload as CallScriptPayload} />;
  }
  if (tool.name === 'audit_url') {
    return <AuditCard payload={tool.payload as AuditPayload} />;
  }
  return null;
}

interface PostIdeaPayload {
  idea?: { title: string; hook: string; cta: string; platform: string; format: string; day: string; pillar: string };
  error?: string;
}
function PostIdeaCard({ payload }: { payload: PostIdeaPayload }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  if (payload.error || !payload.idea) {
    return <ErrorBanner text={payload.error ?? 'Pas d\'idée disponible.'} />;
  }
  const { idea } = payload;
  function copy(): void {
    const txt = `${idea.title}\n\nAccroche : ${idea.hook}\n\nCTA : ${idea.cta}`;
    void navigator.clipboard.writeText(txt).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }
  return (
    <div className="rounded-2xl border border-violet/30 bg-gradient-to-br from-violet/[0.05] to-pink/[0.04] p-3.5">
      <div className="mb-1.5 flex items-center gap-1.5">
        <Lightbulb size={12} className="text-violet" />
        <span className="font-mono text-[9.5px] font-700 uppercase tracking-[0.12em] text-violet">
          Idée · {idea.pillar} · {idea.platform}
        </span>
      </div>
      <p className="font-display text-[14px] font-800 leading-[1.25] tracking-[-0.015em] text-ink">
        {idea.title}
      </p>
      <div className="mt-2 rounded-xl bg-white/70 p-2.5">
        <p className="font-mono text-[9px] font-700 uppercase tracking-[0.12em] text-ink/40">
          Accroche
        </p>
        <p className="mt-0.5 text-[12.5px] leading-[1.5] text-ink/80">« {idea.hook} »</p>
      </div>
      <div className="mt-2 flex items-center justify-between gap-2 text-[11.5px]">
        <p className="text-ink/55">
          <span className="font-700 text-pink">{t('common.ctaLabel')}</span> {idea.cta}
        </p>
        <button
          onClick={copy}
          className="inline-flex items-center gap-1 rounded-md bg-white px-2 py-1 font-700 text-ink/65 transition hover:text-pink"
        >
          {copied ? <Check size={11} /> : <Copy size={11} />}
          {copied ? 'Copié' : 'Copier'}
        </button>
      </div>
    </div>
  );
}

interface RepliesPayload {
  prospect_name?: string;
  suggestions?: { angle: string; text: string }[];
  error?: string;
}
function RepliesCard({ payload }: { payload: RepliesPayload }) {
  if (payload.error || !payload.suggestions?.length) {
    return <ErrorBanner text={payload.error ?? 'Pas de suggestion.'} />;
  }
  return (
    <div className="space-y-1.5 rounded-2xl border border-pink/30 bg-pink/[0.04] p-3">
      <div className="flex items-center gap-1.5">
        <MessageCircle size={12} className="text-pink" />
        <span className="font-mono text-[9.5px] font-700 uppercase tracking-[0.12em] text-pink">
          3 réponses pour {payload.prospect_name ?? 'ce prospect'}
        </span>
      </div>
      {payload.suggestions.map((s, i) => (
        <SuggestionLine key={i} angle={s.angle} text={s.text} />
      ))}
    </div>
  );
}

function SuggestionLine({ angle, text }: { angle: string; text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="rounded-xl bg-white/85 p-2.5">
      <div className="mb-1 flex items-center justify-between">
        <span className="font-mono text-[9px] font-700 uppercase tracking-[0.12em] text-pink">
          {angle}
        </span>
        <button
          onClick={() => {
            void navigator.clipboard.writeText(text).then(() => {
              setCopied(true);
              setTimeout(() => setCopied(false), 1500);
            });
          }}
          className="inline-flex items-center gap-1 rounded-md bg-ink/[0.05] px-1.5 py-0.5 text-[10px] font-700 text-ink/55 transition hover:text-pink"
        >
          {copied ? <Check size={9} /> : <Copy size={9} />}
        </button>
      </div>
      <p className="text-[12.5px] leading-[1.45] text-ink/85">{text}</p>
    </div>
  );
}

interface CallScriptPayload {
  prospect_name?: string;
  prospect_id?: string;
  script?: {
    objective: string;
    opening: string;
    phases: { name: string; duration: string; bullets: string[] }[];
    close: string;
  };
  error?: string;
}
function CallScriptCard({ payload }: { payload: CallScriptPayload }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  if (payload.error || !payload.script) {
    return <ErrorBanner text={payload.error ?? 'Pas de script.'} />;
  }
  return (
    <div className="rounded-2xl border border-blue/30 bg-gradient-to-br from-blue/[0.05] to-violet/[0.04] p-3.5">
      <div className="mb-1.5 flex items-center gap-1.5">
        <Phone size={12} className="text-blue" />
        <span className="font-mono text-[9.5px] font-700 uppercase tracking-[0.12em] text-blue">
          Script d'appel · {payload.prospect_name ?? 'prospect'}
        </span>
      </div>
      <p className="text-[12.5px] leading-[1.5] text-ink/85">{payload.script.objective}</p>
      <div className="mt-2 space-y-1">
        {payload.script.phases.map((ph, i) => (
          <p key={i} className="text-[11.5px] text-ink/70">
            <span className="font-700">{ph.name}</span> ({ph.duration}) — {ph.bullets[0]}…
          </p>
        ))}
      </div>
      {payload.prospect_id && (
        <button
          onClick={() => navigate('/prospects')}
          className="mt-3 inline-flex items-center gap-1 rounded-full bg-white px-3 py-1.5 text-[11.5px] font-700 text-blue transition hover:bg-blue hover:text-white"
        >
          {t('inea.viewFullScript')}
        </button>
      )}
    </div>
  );
}

interface AuditPayload {
  analysis?: { headline: string; score: number; temperature: string; category: string };
  platform?: string;
  url?: string;
  error?: string;
}
function AuditCard({ payload }: { payload: AuditPayload }) {
  const navigate = useNavigate();
  if (payload.error || !payload.analysis) {
    return <ErrorBanner text={payload.error ?? 'Audit indisponible.'} />;
  }
  const a = payload.analysis;
  return (
    <div className="rounded-2xl border border-pink/30 bg-pink/[0.05] p-3.5">
      <div className="mb-1 flex items-center gap-1.5">
        <Target size={12} className="text-pink" />
        <span className="font-mono text-[9.5px] font-700 uppercase tracking-[0.12em] text-pink">
          Audit · {payload.platform}
        </span>
        <span className="ml-auto rounded-md bg-pink/15 px-1.5 py-0.5 font-display text-[11px] font-800 text-pink">
          {a.score}/100
        </span>
      </div>
      <p className="font-display text-[13px] font-800 leading-[1.25] tracking-[-0.015em] text-ink">
        {a.headline}
      </p>
      <button
        onClick={() => navigate('/prospects')}
        className="mt-2 inline-flex items-center gap-1 text-[11px] font-700 text-pink transition hover:underline"
      >
        Voir le rapport complet →
      </button>
    </div>
  );
}

function ErrorBanner({ text }: { text: string }) {
  return (
    <p className="rounded-xl bg-amber-500/10 px-3 py-2 text-[12px] font-600 text-amber-700">
      {text}
    </p>
  );
}
